import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, ExternalLink, Key, Shield, Fingerprint, RefreshCw, User, Download, Upload } from 'lucide-react';
import { notify } from '../utils/toast';
import { createBackup, restoreBackup } from '../utils/backup';
import { storage } from '../lib/storage';
import { useAuth } from '../contexts/AuthContext';
// import { useAuth } from "../hooks/chat/useAuth";
import { refreshProfile } from '../utils/profile';
import { CreditPurchaseDialog } from '../components/credits/CreditPurchaseDialog';
import { formatWalletAddress } from '../utils/wallet';
import { useChat } from '../hooks';

const getAvatarUrl = (profile: any): string | null => {
  try {
    const avatarIpfs = profile?.nfd?.properties?.verified?.avatar;
    if (avatarIpfs) {
      return avatarIpfs.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }
    return null;
  } catch {
    return null;
  }
};

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { state: { user } } = useAuth();
  const [profile, setProfile] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = React.useState(false);

  const loadProfile = async () => {
    if (!user?.address) {
      navigate('/', { replace: true });
      return;
    }

    try {
      const profileData = await storage.getProfile(user.address);
      setProfile(profileData);
    } catch (error) {
      console.error('Error loading profile:', error);
      notify.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadProfile();
  }, [user, navigate]);

  // Update profile when credits change
  const handleCreditUpdate = React.useCallback((newCredit: number) => {
    setProfile((prev: any) => prev ? { ...prev, credit: newCredit } : null);
  }, []);

  // Initialize chat hook with credit update handler
  useChat(handleCreditUpdate);

  const handleRefresh = useCallback(async () => {
    if (!user?.address || refreshing) return;

    setRefreshing(true);
    try {
      const updatedProfile = await refreshProfile(user.address);
      if (updatedProfile) {
        setProfile(updatedProfile);
      }
    } finally {
      setRefreshing(false);
    }
  }, [user, refreshing]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notify.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-pulse text-gray-600 dark:text-gray-400">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">No profile data found</div>
      </div>
    );
  }

  const nfdName = localStorage.getItem('nfdName');
  const walletBalance = localStorage.getItem('walletBalance');
  const walletMinBalance = localStorage.getItem('walletMinBalance');
  const walletRekeyTo = localStorage.getItem('walletRekeyTo');
  const avatarUrl = getAvatarUrl(profile);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">User Profile</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage your profile and wallet information</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing...' : 'Sync Profile'}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Header with Avatar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-500 to-purple-500 p-0.5">
                    <div className="w-full h-full rounded-full overflow-hidden bg-white dark:bg-gray-800 p-1">
                      {avatarUrl ? (
                        <img 
                          src={avatarUrl}
                          alt="Profile"
                          className="w-full h-full object-cover rounded-full"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement?.classList.add('fallback-icon');
                          }}
                        />
                      ) : (
                        <div className="w-full h-full rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <User className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {nfdName || formatWalletAddress(user?.address || '')}
                    </h2>
                    {nfdName && (
                      <a
                        href={`https://${nfdName}.xyz`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm flex items-center space-x-1"
                      >
                        <span>{nfdName}</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Wallet Information */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Wallet Information</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Address</span>
                    <button
                      onClick={() => copyToClipboard(user?.address || '')}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-sm text-gray-900 dark:text-white font-mono break-all bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                    {user?.address}
                  </div>
                </div>

                {nfdName && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">NFD Name</span>
                      <a
                        href={`https://${nfdName}.xyz`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                      {nfdName}
                    </div>
                  </div>
                )}

                {walletBalance && walletMinBalance && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-2">Balance</span>
                      <div className="text-sm text-gray-900 dark:text-white font-medium bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                        {(Number(walletBalance) / 1e6).toFixed(6)} ALGO
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-2">Min Balance</span>
                      <div className="text-sm text-gray-900 dark:text-white font-medium bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                        {(Number(walletMinBalance) / 1e6).toFixed(6)} ALGO
                      </div>
                    </div>
                  </div>
                )}

                {walletRekeyTo && (
                  <div className="mt-4">
                    <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400 mb-2">
                      <Key className="w-4 h-4" />
                      <span className="text-sm font-medium">Rekeyed Account</span>
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white font-mono break-all bg-amber-50 dark:bg-amber-900/30 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                      {walletRekeyTo}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Credits Information */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">AI Credits</h2>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      {profile.credit || 0}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Credits remaining
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPurchaseDialog(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Buy More Credits
                  </button>
                </div>
              </div>
            </div>

            {/* Backup Section */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Backup</h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex space-x-4">
                  <button
                    onClick={async () => {
                      try {
                        const backup = await createBackup(user?.address || '');
                        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Agency Backup ${new Date().toISOString()}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        notify.success('Backup created successfully');
                      } catch (error) {
                        console.error('Backup failed:', error);
                        notify.error('Failed to create backup');
                      }
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Backup</span>
                  </button>
                  <label className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span>Restore</span>
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        try {
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            try {
                              const backupData = JSON.parse(event.target?.result as string);
                              const result = await restoreBackup(user?.address || '', backupData);
                              notify.success(`Restored ${result.newConversations} conversations and ${result.newMessages} messages`);
                            } catch (error) {
                              console.error('Restore failed:', error);
                              notify.error('Failed to restore backup: Invalid backup file');
                            }
                          };
                          reader.readAsText(file);
                        } catch (error) {
                          console.error('Restore failed:', error);
                          notify.error('Failed to read backup file');
                        }
                      }}
                    />
                  </label>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Backup and restore your conversations and messages. The backup file includes all your chat history.
                </p>
              </div>
            </div>

            {/* DID Information */}
            {profile.did && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Decentralized Identity</h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Fingerprint className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">DID</span>
                  </div>
                  <div className="text-sm text-gray-900 dark:text-white font-mono break-all bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                    {profile.did}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Authentication Status */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Authentication</h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center space-x-3 text-green-600 dark:text-green-400">
                  <Shield className="w-5 h-5" />
                  <span className="text-sm font-medium">Verified Account</span>
                </div>
                
                {profile.jwt && (
                  <div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Session Token
                    </div>
                    <div className="text-xs font-mono text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 rounded p-2 break-all">
                      {profile.jwt.substring(0, 20)}...
                    </div>
                  </div>
                )}

                {profile.nonce && (
                  <div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Nonce
                    </div>
                    <div className="text-xs font-mono text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 rounded p-2">
                      {profile.nonce}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreditPurchaseDialog
        isOpen={showPurchaseDialog}
        onClose={() => setShowPurchaseDialog(false)}
      />
    </div>
  );
};
